import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, Loader2, ChevronRight } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);
const fmtDW = (s) => new Date(s + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const STATUS = {
  ok: { label: "OK", cls: "text-emerald-600 dark:text-emerald-400" },
  partial: { label: "Some 0 rooms", cls: "text-amber-600 dark:text-amber-400 font-semibold" },
  missing_rooms: { label: "Hours, no rooms", cls: "text-red-600 dark:text-red-400 font-semibold" },
  no_payroll: { label: "Not saved", cls: "text-muted-foreground" },
};
const ROW_BG = { missing_rooms: "bg-red-50 dark:bg-red-950/20", partial: "bg-amber-50 dark:bg-amber-950/20", no_payroll: "opacity-60" };

function DayRow({ r }) {
  const [open, setOpen] = useState(false);
  const st = STATUS[r.status];
  return (
    <>
      <tr className={`cursor-pointer border-t border-border hover:bg-muted/40 ${ROW_BG[r.status] || ""}`} onClick={() => setOpen((v) => !v)} data-testid={`rr-day-${r.date}`}>
        <td className="whitespace-nowrap px-3 py-1.5"><ChevronRight className={`mr-1 inline h-3.5 w-3.5 transition-transform ${open ? "rotate-90" : ""}`} />{fmtDW(r.date)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{r.housekeepers.length}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{r.hours.toFixed(2)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{r.stayover}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{r.checkout}</td>
        <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{r.rooms}</td>
        <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{r.prev_rooms_sold ?? "—"}</td>
        <td className={`px-3 py-1.5 text-right text-xs ${st.cls}`}>{st.label}</td>
      </tr>
      {open && r.housekeepers.map((x) => (
        <tr key={x.name} className="bg-muted/20 text-xs">
          <td className="py-1 pl-9 pr-3" colSpan={2}>{x.name}</td>
          <td className="px-3 py-1 text-right tabular-nums">{x.hours.toFixed(2)}</td>
          <td className="px-3 py-1 text-right tabular-nums">{x.total_only ? "—" : x.stayover}</td>
          <td className="px-3 py-1 text-right tabular-nums">{x.total_only ? "—" : x.checkout}</td>
          <td className={`px-3 py-1 text-right font-semibold tabular-nums ${x.hours > 0 && !x.rooms ? "text-red-600" : ""}`}>{x.rooms}{x.total_only && <span className="ml-1 text-[0.55rem] font-normal uppercase text-muted-foreground">total</span>}</td>
          <td colSpan={2} />
        </tr>
      ))}
      {open && r.updated_by && <tr className="bg-muted/20 text-[0.65rem] text-muted-foreground"><td colSpan={8} className="py-1 pl-9">Saved by {r.updated_by}</td></tr>}
    </>
  );
}

function PropertyBlock({ p }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-border bg-card" data-testid={`rr-prop-${p.property_id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40">
        <span className="flex items-center gap-2 font-semibold"><ChevronRight className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`} />{p.property_name}</span>
        <span className="flex items-center gap-3 text-xs">
          <span className="font-mono">{p.total_rooms} rooms</span>
          <span className={`rounded-full px-2 py-0.5 font-bold ${p.problem_days ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400" : "bg-emerald-500/15 text-emerald-600"}`}>
            {p.problem_days ? `${p.problem_days} day${p.problem_days > 1 ? "s" : ""} to check` : "All OK"}
          </span>
        </span>
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-border">
          <table className="w-full text-sm">
            <thead><tr className="bg-muted/50 text-left text-[0.65rem] uppercase tracking-wide text-muted-foreground">
              <th className="px-3 py-2">Date</th><th className="px-3 py-2 text-right">Housekeepers</th><th className="px-3 py-2 text-right">Hours</th>
              <th className="px-3 py-2 text-right">Stayover</th><th className="px-3 py-2 text-right">Checkout</th><th className="px-3 py-2 text-right">Rooms</th>
              <th className="px-3 py-2 text-right">Occupied prev night</th><th className="px-3 py-2 text-right">Status</th>
            </tr></thead>
            <tbody>{p.days.map((r) => <DayRow key={r.date} r={r} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export const HkRoomsReport = ({ propertyId, allowAll }) => {
  const [range, setRange] = useState([iso(new Date(Date.now() - 90 * 864e5)), iso(new Date(Date.now() - 864e5))]);
  const [scope, setScope] = useState(allowAll ? "all" : "one");
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const params = { start: range[0], end: range[1], ...(scope === "one" ? { property_id: propertyId } : {}) };
  const key = JSON.stringify(params);

  const load = useCallback(() => {
    setData(null);
    api.get("/housekeeping/rooms-report", { params: JSON.parse(key) }).then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load report"); setData({ properties: [] }); });
  }, [key]);
  useEffect(() => { if (scope === "all" || propertyId) load(); }, [load, scope, propertyId]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/housekeeping/rooms-report", { params: { ...params, export: true }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `HK_Rooms_Cleaned_${range[0]}_to_${range[1]}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="hk-rooms-report">
      <div className="flex flex-wrap items-end gap-3">
        <Input type="date" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} className="w-40" data-testid="rr-start" />
        <span className="pb-2 text-muted-foreground">–</span>
        <Input type="date" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} className="w-40" data-testid="rr-end" />
        {allowAll && (
          <div className="flex gap-1">
            <Button size="sm" variant={scope === "all" ? "default" : "outline"} onClick={() => setScope("all")} data-testid="rr-scope-all">All properties</Button>
            <Button size="sm" variant={scope === "one" ? "default" : "outline"} onClick={() => setScope("one")} data-testid="rr-scope-one">This property</Button>
          </div>
        )}
        <Button className="ml-auto" size="sm" variant="outline" onClick={exportXlsx} disabled={!data || exporting} data-testid="rr-export">
          {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export Excel
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">Rooms cleaned by housekeepers each day, from the Daily Report. Red = housekeeping hours but no rooms, amber = some housekeepers have 0 rooms. Click a day to see each housekeeper. "Total" = room count without a stayover/checkout split (e.g. restored).</p>
      {data === null ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        : data.properties.length === 0 ? <p className="text-sm text-muted-foreground">No properties.</p>
        : data.properties.map((p) => <PropertyBlock key={p.property_id} p={p} />)}
    </div>
  );
};
